import { Box, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import CapCell from './CapCell';
import Avatar from './Avatar';
import { percentUsed } from '../utils';
import { barTrackSx, BORDER, bodySx, captionSx, DANGER, eyebrowSx, headingSx, INK, MUTED, SOFT } from './styles';

const headSx = { ...eyebrowSx, fontSize: '11px', py: 1.25, borderBottom: `1px solid ${BORDER}`, backgroundColor: SOFT, whiteSpace: 'nowrap' };
const cellSx = { ...bodySx, py: 1.25, borderBottom: `1px solid ${BORDER}`, verticalAlign: 'middle' };

function UsedCell({ used, limit }) {
  const pct = percentUsed(used, limit);
  return (
    <Box sx={{ minWidth: 90 }}>
      <Typography component="div" sx={{ ...bodySx, fontWeight: 600, color: INK }}>
        <span>{used}</span>
        {limit !== null && limit !== undefined && (
          <Box component="span" sx={{ fontWeight: 400, color: MUTED }}>{` of ${limit}`}</Box>
        )}
      </Typography>
      {pct !== null && (
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ ...barTrackSx, height: 4, mt: 0.75, maxWidth: 110, '& .MuiLinearProgress-bar': { borderRadius: '99px', backgroundColor: pct >= 100 ? DANGER : 'primary.main' } }}
        />
      )}
    </Box>
  );
}

// Shared table for team carves and user allowances: one row per subject with
// its usage and an editable cap. rows: [{ id, label, secondary, used, cap, limit }],
// where limit is what `used` is measured against (the cap, or the default).
export default function CapTable({ testId, heading, description, nameColumn, capColumn, rows, emptyHint, isBusy, onSave, onRemove, avatars }) {
  return (
    <Box data-testid={testId}>
      <Typography sx={{ ...headingSx, mb: description ? 0.25 : 1.25 }}>{heading}</Typography>
      {description && <Typography sx={{ ...captionSx, mb: 1.25 }}>{description}</Typography>}
      {rows.length === 0 ? (
        <Box sx={{ border: `1px dashed ${BORDER}`, borderRadius: '10px', p: 2, textAlign: 'center' }}>
          <Typography sx={captionSx}>{emptyHint}</Typography>
        </Box>
      ) : (
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 520, '& tr:last-of-type td': { borderBottom: 0 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>{nameColumn}</TableCell>
                <TableCell sx={headSx}>Used</TableCell>
                <TableCell sx={headSx}>{capColumn}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      {avatars && <Avatar name={row.label} email={row.secondary} />}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ ...bodySx, fontWeight: 500, color: INK }}>{row.label}</Typography>
                        {row.secondary && <Typography sx={captionSx}>{row.secondary}</Typography>}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <UsedCell used={row.used} limit={row.limit} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <CapCell
                      label={row.label}
                      stored={row.cap}
                      placeholder={row.placeholder}
                      busy={isBusy(row.id)}
                      onSave={(cap) => onSave(row, cap)}
                      onRemove={() => onRemove(row)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
